import React, { memo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import { LICENSE_MILESTONES, fillLicenseText, formatLicenseDate } from "../../utils/LicenseExpire";
import { levelColors, SENT_COLORS } from "./licenseStyle";

const TITLE_ID = "license-notice-title";

const useStyles = makeStyles({
  content: { position: "relative", padding: "22px 24px 4px", "&:first-child": { paddingTop: 22 } },
  close: { position: "absolute", top: 8, right: 8, color: "#9ca3af" },
  icon: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
  },
  title: { fontSize: 17, fontWeight: 500, lineHeight: 1.4, color: "#1f2937", margin: "12px 32px 0 0" },
  remaining: { display: "flex", alignItems: "baseline", marginTop: 10 },
  remainingLabel: { fontSize: 13, color: "#6b7280", marginRight: 8 },
  remainingDays: { fontSize: 40, fontWeight: 500, lineHeight: 1, marginRight: 8 },
  remainingUnit: { fontSize: 15, color: "#1f2937" },
  expireOn: { fontSize: 13, color: "#6b7280", marginTop: 6 },
  track: { display: "flex", marginTop: 14 },
  segment: { flex: 1, height: 4, borderRadius: 2, "&:not(:last-child)": { marginRight: 4 } },
  trackLabels: { display: "flex", marginTop: 4, fontSize: 11, color: "#9ca3af", "& > span": { flex: 1 } },
  message: { fontSize: 14, lineHeight: 1.6, color: "#374151", margin: "14px 0 0" },
  info: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.6,
    color: "#6b7280",
    marginTop: 12,
  },
  error: { fontSize: 13, color: "#A32D2D", marginTop: 10 },
  actions: { padding: "16px 24px 20px" },
  laterButton: { color: "#374151" },
  spinner: { marginRight: 8 },
});

const noticeTitle = (status, t) => {
  if (status.isExpired) return t("ISA_License_Title_Expired", "License หมดอายุแล้ว");
  if (status.remainingDays === 0) return t("ISA_License_Title_LastDay", "License หมดอายุวันนี้");
  if (status.remainingDays === 1) return t("ISA_License_Title_Tomorrow", "License จะหมดอายุพรุ่งนี้");
  if (status.level === "danger") return t("ISA_License_Title_Danger", "License ใกล้หมดอายุมาก");
  if (status.level === "warning") return t("ISA_License_Title_Warning", "License ใกล้หมดอายุ");
  return t("ISA_License_Title_Info", "แจ้งเตือนการต่ออายุ License");
};

const noticeMessage = (status, t) => {
  if (status.isExpired) {
    return t("ISA_License_Message_Expired", "License ของบริษัทหมดอายุแล้ว ผู้ใช้จะเข้าสู่ระบบใหม่ไม่ได้จนกว่าจะต่ออายุ กรุณาแจ้งต่ออายุเพื่อใช้งานต่อ");
  }
  if (status.level === "danger") {
    return t("ISA_License_Message_Danger", "กรุณาแจ้งต่ออายุโดยเร็ว เมื่อ License หมดอายุแล้วจะไม่สามารถเข้าสู่ระบบได้");
  }
  if (status.level === "warning") {
    return t("ISA_License_Message_Warning", "ระบบยังใช้งานได้ตามปกติ แนะนำให้แจ้งต่ออายุตอนนี้ เพื่อให้ทีมงานดำเนินการได้ทันก่อนวันหมดอายุ");
  }
  return t("ISA_License_Message_Info", "ระบบยังใช้งานได้ตามปกติ แจ้งต่ออายุไว้ล่วงหน้า เพื่อให้ทีมของคุณใช้งานต่อได้ไม่สะดุด");
};

/** popup แจ้งเตือน License — ปิดได้ทุกเมื่อ ไม่บล็อกการใช้งาน */
const LicenseDialog = ({ open, status, t, sending, error, onClose, onRequest }) => {
  const classes = useStyles();
  const colors = levelColors(status.level);
  const requested = !!status.renewRequest;

  const expireOn = fillLicenseText(t("ISA_License_ExpireOn", "หมดอายุวันที่ {date}"), {
    date: formatLicenseDate(status.expireDate),
  });
  const company = status.companyName ? ` · ${status.companyName}` : "";

  //ช่วงที่ผ่านมาแล้วระบายสีตามระดับ — หมดอายุแล้วระบายเต็มแถบ
  const currentIndex = status.isExpired ? LICENSE_MILESTONES.length - 1 : LICENSE_MILESTONES.indexOf(status.milestone);

  const noticeView = (
    <>
      <div className={classes.icon} style={{ background: colors.tint, color: colors.ink }}>
        <i className={`zmdi ${status.level === "danger" || status.isExpired ? "zmdi-alert-triangle" : "zmdi-calendar"}`} />
      </div>
      <h2 id={TITLE_ID} className={classes.title}>{noticeTitle(status, t)}</h2>
      {status.remainingDays > 0 ? (
        <div className={classes.remaining}>
          <span className={classes.remainingLabel}>{t("ISA_License_RemainingLabel", "เหลือเวลาอีก")}</span>
          <span className={classes.remainingDays} style={{ color: colors.ink }}>{status.remainingDays}</span>
          <span className={classes.remainingUnit}>{t("ISA_License_Days", "วัน")}</span>
        </div>
      ) : null}
      <div className={classes.expireOn}>{expireOn}{company}</div>
      <div className={classes.track}>
        {LICENSE_MILESTONES.map((milestone, index) => (
          <span key={milestone} className={classes.segment} style={{ background: index <= currentIndex ? colors.dot : "#e5e7eb" }} />
        ))}
      </div>
      <div className={classes.trackLabels}>
        {LICENSE_MILESTONES.map((milestone) => <span key={milestone}>{milestone}</span>)}
      </div>
      <p className={classes.message}>{noticeMessage(status, t)}</p>
      {error ? <div className={classes.error} role="alert">{error}</div> : null}
    </>
  );

  const sentView = requested ? (
    <>
      <div className={classes.icon} style={{ background: SENT_COLORS.tint, color: SENT_COLORS.ink }}>
        <i className="zmdi zmdi-check" />
      </div>
      <h2 id={TITLE_ID} className={classes.title}>{t("ISA_License_Sent_Title", "ส่งคำขอต่ออายุแล้ว")}</h2>
      <p className={classes.message}>
        {t("ISA_License_Sent_Message", "ทีมงานได้รับคำขอของบริษัทคุณแล้ว และจะติดต่อกลับเพื่อดำเนินการต่ออายุ")}
      </p>
      <div className={classes.expireOn}>{expireOn}{company}</div>
      <div className={classes.info}>
        {fillLicenseText(t("ISA_License_Sent_Info", "ส่งเมื่อ {date} · โดย {name}"), {
          date: formatLicenseDate(status.renewRequest.requestedAt),
          name: status.renewRequest.requestedByName || status.renewRequest.requestedBy,
        })}
        <br />
        {t("ISA_License_Sent_Shared", "ผู้ใช้คนอื่นในบริษัทจะเห็นสถานะนี้ ไม่ต้องส่งซ้ำ")}
      </div>
    </>
  ) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby={TITLE_ID}>
      <DialogContent className={classes.content}>
        <IconButton className={classes.close} size="small" onClick={onClose} aria-label={t("ISA_License_Close", "ปิด")}>
          <i className="zmdi zmdi-close" />
        </IconButton>
        {requested ? sentView : noticeView}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {requested ? (
          <Button onClick={onClose} className={classes.laterButton}>{t("ISA_License_Close", "ปิด")}</Button>
        ) : (
          <>
            <Button onClick={onClose} className={classes.laterButton}>{t("ISA_License_Later", "ไว้ภายหลัง")}</Button>
            <Button variant="contained" color="primary" disableElevation onClick={onRequest} disabled={sending}>
              {sending ? <CircularProgress size={16} color="inherit" className={classes.spinner} /> : null}
              {t("ISA_License_Request", "แจ้งขอต่ออายุ")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

LicenseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  status: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  sending: PropTypes.bool,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRequest: PropTypes.func.isRequired,
};

LicenseDialog.defaultProps = {
  sending: false,
  error: "",
};

export default memo(LicenseDialog);
